// frontend/src/ts/protocols/persistence/tauriWriteStore.ts
import {invokeTauriCommand} from '@/common/utils/tauri'
import {PendingSummary, PendingWrite, WriteType} from './types'
import {ClaimArgs, EnqueueArgs, GetPendingWritesArgs, WriteStore} from './writeStore'

// In-memory subscriber map keyed by `${companyId}:${protocolId}`.
// Handlers are session-only; they do not persist across reloads.
const persistHandlers = new Map<string, Set<(rowId: number, coalesceKey: string | null, writeType: WriteType, response: unknown) => void>>()

function persistHandlerKey(companyId: string, protocolId: string): string {
    return `${companyId}:${protocolId}`
}

/** Test-only hook to reset the module-level subscriber map. Not exported
 *  from the store's public surface — call from test beforeEach. */
export function __clearPersistHandlersForTests(): void {
    persistHandlers.clear()
}

// ── Rust ↔ JS wire-format mapping ─────────────────────────────────────
//
// Every Tauri command in the pending-writes surface takes its
// domain payload as a single `args: <Struct>` parameter, with the
// inner struct using snake_case keys (Rust uses
// `#[serde(rename_all = "snake_case", deny_unknown_fields)]`).
// The JS-side wrapper below builds the `{ args: { ... } }` payload
// with snake_case keys explicitly — Tauri's top-level camelCase
// auto-rename does not reach nested struct fields.
//
// `payload` and `changes` are STRICT pass-through: the Rust
// deserializer accepts any JSON shape under those keys, stores it
// as TEXT in SQLite, and never inspects the inner contents. The
// JS side sends arbitrary IProtocolStep / IProtocol /
// IProtocolStepEvent shapes without transformation.

type RustPendingWrite = {
    id: number
    coalesce_key: string | null
    step_id: string | null
    write_type: WriteType
    payload: Record<string, unknown>
    // User-edit delta for step/protocol updates; null for create/delete.
    changes: Record<string, unknown> | null
    created_at: number
    last_attempt_at: number | null
    attempt_count: number
    last_error: string | null
    state: PendingWrite['state']
}

function pendingWriteFromRust(r: RustPendingWrite): PendingWrite {
    return {
        id: r.id,
        coalesceKey: r.coalesce_key,
        stepId: r.step_id,
        writeType: r.write_type,
        payload: r.payload,
        changes: r.changes ?? undefined,
        createdAt: r.created_at,
        lastAttemptAt: r.last_attempt_at,
        attemptCount: r.attempt_count,
        lastError: r.last_error,
        state: r.state,
    }
}

// PendingSummary's wire fields are identical to the JS interface
// (pending, dead, superseded, persisted — all single-word lowercase),
// so no mapping is needed.

/**
 * Tauri-backed WriteStore: each method maps to an NDC Tauri command
 * (see ndc/src-tauri/src/persistence/commands.rs).
 */
export class TauriWriteStore implements WriteStore {
    // Per-protocol FIFO chain for enqueue. Burst writes (Skip All fires
    // N+1 enqueues in one loop) execute one at a time instead of as N+1
    // parallel IPC calls — this removes IPC/SQLite write-lock contention
    // between the enqueues themselves. The drainer's claim/mark calls do
    // not go through this chain; serializing enqueues only reduces the
    // overlap with them. Cross-key ordering is not semantically required
    // (claim orders per coalesce_key); this is purely contention removal.
    // Failures do not block the chain — each caller still observes its
    // own rejection.
    private enqueueChains = new Map<string, Promise<void>>()

    async enqueue(args: EnqueueArgs): Promise<number> {
        const k = persistHandlerKey(args.companyId, args.protocolId)
        const prev = this.enqueueChains.get(k) ?? Promise.resolve()
        const current = prev.then(() => this.enqueueIpc(args))
        // Chain link swallows the rejection so one failed enqueue cannot
        // wedge the rest of the queue.
        this.enqueueChains.set(k, current.then(() => undefined, () => undefined))
        return current
    }

    private async enqueueIpc(args: EnqueueArgs): Promise<number> {
        // The Rust enqueue_write command runs the coalesce cascade
        // (supersedes prior pending/dead rows for the same
        // coalesce_key + write_type). It does NOT cascade across
        // different write types — an event_delete for a coalesce_key
        // with a pending event_create does NOT supersede the create;
        // both reach the drainer, the server creates then deletes.
        // See ndc/src-tauri/src/persistence/commands.rs for the SQL.
        // `payload` and `changes` are strict pass-through JSON — the
        // args struct's `rename_all = "snake_case"` only renames the
        // outer field NAME; the inner contents are stored verbatim
        // in SQLite TEXT.
        try {
            return await invokeTauriCommand<number>('enqueue_write', {
                args: {
                    company_id: args.companyId,
                    protocol_id: args.protocolId,
                    coalesce_key: args.coalesceKey,
                    step_id: args.stepId,
                    write_type: args.writeType,
                    payload: args.payload,
                    changes: args.changes ?? null,
                },
            })
        } catch (e) {
            // IPC/SQLite failure site. Rethrown for the coordinator to
            // notify and skip the React-store apply.
            console.error('[tauriWriteStore] enqueue_write failed', {
                coalesceKey: args.coalesceKey,
                stepId: args.stepId,
                writeType: args.writeType,
            }, e)
            throw e
        }
    }

    async claim(args: ClaimArgs): Promise<PendingWrite[]> {
        const rows = await invokeTauriCommand<RustPendingWrite[]>('claim_writes', {
            args: {
                company_id: args.companyId,
                protocol_id: args.protocolId,
                backoff_cutoff_ms: args.backoffCutoffMs,
                limit: args.limit,
            },
        })
        return rows.map(pendingWriteFromRust)
    }

    async markPersisted(companyId: string, protocolId: string, id: number): Promise<void> {
        await invokeTauriCommand('mark_write_persisted', {
            args: {company_id: companyId, protocol_id: protocolId, id},
        })
    }

    async markFailed(companyId: string, protocolId: string, id: number, error: string, retryable: boolean): Promise<void> {
        await invokeTauriCommand('mark_write_failed', {
            args: {company_id: companyId, protocol_id: protocolId, id, error, retryable},
        })
    }

    async retryWrite(companyId: string, protocolId: string, id: number): Promise<void> {
        await invokeTauriCommand('retry_write', {
            args: {company_id: companyId, protocol_id: protocolId, id},
        })
    }

    async retryAllDead(companyId: string, protocolId: string): Promise<number[]> {
        return await invokeTauriCommand<number[]>('retry_all_dead', {
            args: {company_id: companyId, protocol_id: protocolId},
        })
    }

    async summary(companyId: string, protocolId: string): Promise<PendingSummary> {
        return await invokeTauriCommand<PendingSummary>('get_pending_summary', {
            args: {company_id: companyId, protocol_id: protocolId},
        })
    }

    async getPendingWrites(args: GetPendingWritesArgs): Promise<PendingWrite[]> {
        // `GetPendingWritesQueryArgs` flattens the previous nested
        // `args: { coalesce_view, state_filter, limit }` into a single
        // top-level args struct alongside the routing keys. All
        // snake_case.
        const rows = await invokeTauriCommand<RustPendingWrite[]>('get_pending_writes', {
            args: {
                company_id: args.companyId,
                protocol_id: args.protocolId,
                coalesce_view: args.coalesceView,
                state_filter: args.stateFilter,
                limit: args.limit ?? null,
            },
        })
        return rows.map(pendingWriteFromRust)
    }

    onWritePersisted(
        companyId: string,
        protocolId: string,
        handler: (rowId: number, coalesceKey: string | null, writeType: WriteType, response: unknown) => void,
    ): () => void {
        const k = persistHandlerKey(companyId, protocolId)
        let set = persistHandlers.get(k)
        if (!set) {
            set = new Set()
            persistHandlers.set(k, set)
        }
        set.add(handler)
        return () => {
            const s = persistHandlers.get(k)
            if (!s) return
            s.delete(handler)
            if (s.size === 0) persistHandlers.delete(k)
        }
    }

    notifyWritePersisted(
        companyId: string,
        protocolId: string,
        rowId: number,
        coalesceKey: string | null,
        writeType: WriteType,
        response: unknown,
    ): Promise<void> {
        const set = persistHandlers.get(persistHandlerKey(companyId, protocolId))
        if (!set || set.size === 0) return Promise.resolve()
        // Snapshot so handlers can unsubscribe during dispatch without
        // mutating the iteration set.
        const snapshot = [...set]
        return Promise.all(
            snapshot.map((h) => Promise.resolve(h(rowId, coalesceKey, writeType, response))),
        ).then(() => undefined)
    }
}
